import { Injectable } from '@angular/core';
import { createStore, select, setProp, withProps } from '@ngneat/elf';
import {
  addEntities,
  deleteEntities,
  getEntity,
  selectAllEntities,
  selectAllEntitiesApply,
  selectEntity,
  setEntities,
  updateEntities,
  withEntities,
} from '@ngneat/elf-entities';
import { persistState } from '@ngneat/elf-persist-state';
import * as localforage from 'localforage';
import * as moment from 'moment';
import { BehaviorSubject, Observable } from 'rxjs';
import { debounceTime, tap, map, switchMap, filter } from 'rxjs/operators';
import { REFRESH_TIME_IN_MINUTES } from 'src/app/shared/configs/global-values.constants';
import { ApiBaseService } from 'src/app/shared/services/api/api-base/api-base.service';
import { resolveMongoObjectId } from 'src/app/shared/utils/resolveMongoObject.utils';
import {
  IDeleteParams,
  IStoreProps,
  IListParams,
  IPaginationState,
  IScrollPosition,
  IPlayersScrollPosition,
} from 'src/app/talent-litmus-core/common/common.interface';
import { ILearningPlayerRank } from 'src/app/talent-litmus-core/learning-player-core';
import { ILearningGame } from './learning-game.interface';
import { PageEvent } from '@angular/material/paginator';

// eslint-disable-next-line @typescript-eslint/naming-convention
// eslint-disable-next-line @typescript-eslint/naming-convention
const STORE_NAME = 'learning-games';

const instance = localforage.createInstance({
  driver: localforage.INDEXEDDB,
  name: 'talent-litmus-adminv2',
  version: 1.0,
  storeName: STORE_NAME,
});

const store = createStore(
  { name: STORE_NAME },
  withProps<IStoreProps>({ lastRefreshed: undefined, search: '' }),
  withProps<IPaginationState>({
    length: 0,
    pageIndex: 0,
    pageSize: 0,
    previousPageIndex: 0,
  }),
  withEntities<ILearningGame, '_id'>({ idKey: '_id' }),
  withProps<IScrollPosition>({ x: 0, y: 0 }),
  withProps<IPlayersScrollPosition>({ cordX: 0, cordY: 0 }),
  withProps<{ selectedId: string }>({ selectedId: null }),
  withProps<{ searchParam: string }>({ searchParam: '' }),
);

export const persist = persistState(store, {
  key: STORE_NAME,
  storage: instance,
});

@Injectable()
export class LearningGameCoreService {
  private _loading$ = new BehaviorSubject<boolean>(false);
  private _filter$ = store.pipe(select(({ search }) => search));

  /**
   * @name learningGameList$
   * @description emits stored list of the companies
   */
  learningGameList$ = store.pipe(selectAllEntities());
  paginationState$ = store.pipe(
    select((state) => {
      return state;
    }),
  );
  totalCount: number;

  visibleLearningGameList$ = this._filter$.pipe(
    switchMap((filter) => {
      return store.pipe(
        selectAllEntitiesApply({
          filterEntity(learningGame: ILearningGame) {
            return (
              learningGame.name
                .toLowerCase()
                .includes(filter.trim().toLowerCase()) ||
              learningGame._id
                .toLowerCase()
                .includes(filter.trim().toLowerCase())
            );
          },
        }),
      );
    }),
  );

  public get loading$(): Observable<boolean> {
    return this._loading$.asObservable();
  }

  constructor(private _api: ApiBaseService) {
    this.refresh();
  }

  public updateFilter(search: IStoreProps['search']): void {
    store.update(setProp('search', search));
  }

  public getLearningGames(params?: IListParams): void {
    this._loading$.next(true);
    this._api
      .get(`master-game/list?type=learning`, params || {})
      .pipe(debounceTime(10000))
      .subscribe((data: ILearningGameListResponse) => {
        this.totalCount = data.totalCount;
        store.update(
          setEntities(data.list),
          setProp('lastRefreshed', new Date()),
        );
        this._loading$.next(false);
      });
  }
  public refresh(forced?: boolean): void {
    if (
      forced ||
      !store.value.lastRefreshed ||
      moment().diff(moment(store.value.lastRefreshed), 'minutes') >
        REFRESH_TIME_IN_MINUTES
    ) {
      this.getLearningGames();
    }
  }

  public getLearningGame(learningGameId: string): Observable<ILearningGame> {
    return this._api.get(`master-game/${learningGameId}`);
  }

  public getLearningGameFromCache(
    learningGameId: string,
  ): Observable<ILearningGame> {
    return store.pipe(selectEntity(learningGameId));
  }

  public addLearningGame(
    learningGame: ILearningGame,
  ): Observable<ILearningGame> {
    return this._api
      .post(`master-game/add`, learningGame)
      .pipe(tap((data: ILearningGame) => store.update(addEntities([data]))));
  }

  public updateLearningGame(
    learningGame: ILearningGame,
  ): Observable<ILearningGame> {
    return this._api.put(`master-game/edit`, learningGame).pipe(
      map((data: ArrayBuffer) => data as any),
      tap((data: ILearningGame) =>
        store.update(
          updateEntities(data._id, (entity) => ({
            ...entity,
            name: data.name,
          })),
        ),
      ),
    );
  }

  public deleteLearningGame(data: IDeleteParams): Observable<any> {
    return this._api
      .delete('master-game', { body: data })
      .pipe(tap(() => store.update(deleteEntities(data['_id']))));
  }

  public getLearningGamePlayers(
    learningGameId: string,
  ): Observable<ILearningPlayerRank[]> {
    return store.pipe(
      selectEntity(learningGameId),
      filter((learningGame: ILearningGame) => !!learningGame),
      switchMap((learningGame) => {
        const divisionId = resolveMongoObjectId(learningGame.divisionId);
        return this._api.get(
          `learning-players/leaderboard?divisionId=${divisionId}&maxRank=100000`,
        );
      }),
    );
  }

  public deleteLearningGamePlayers(
    learningGameId: string,
    _ids: string[],
  ): Observable<any> {
    return store.pipe(
      selectEntity(learningGameId),
      switchMap((learningGame) => {
        const divisionId = resolveMongoObjectId(learningGame.divisionId);
        const body: IBulkDeletePlayer = {
          divisionId,
          _ids,
        };
        return this._api.delete(`learning-players/bulk`, { body });
      }),
    );
  }

  public getLearningGameInfo(id: string): ILearningGame {
    const learningGame = store.query(getEntity(id));
    return learningGame;
  }

  moveToLastPage(): void {
    let length: number;
    let pageSize: number;
    let previousPageIndex: number;
    this.paginationState$.subscribe((state) => {
      length = state.length;
      pageSize = state.pageSize;
      previousPageIndex = state.previousPageIndex;
    });
    this.pageChangeEvent({
      length,
      pageIndex: Math.ceil(length / pageSize),
      pageSize,
      previousPageIndex,
    });
  }

  pageChangeEvent(event: PageEvent): void {
    store.update((state) => ({
      ...state,
      length: event.length,
      pageIndex: event.pageIndex,
      pageSize: event.pageSize,
      previousPageIndex: event.previousPageIndex,
    }));
  }

  setScrollPosition(scrollPostion: [number, number]): void {
    store.update((state) => ({
      ...state,
      x: scrollPostion[0],
      y: scrollPostion[1],
    }));
  }

  setPlayersScrollPosition(scrollPostion: [number, number]): void {
    store.update((state) => ({
      ...state,
      cordX: scrollPostion[0],
      cordY: scrollPostion[1],
    }));
  }

  setSelectedId(id: string): void {
    store.update((state) => ({
      ...state,
      selectedId: id,
    }));
  }

  setSearchParam(searchParam: string): void {
    store.update((state) => ({
      ...state,
      searchParam: searchParam,
    }));
  }
}

interface ILearningGameListResponse {
  list: ILearningGame[];
  totalCount: number;
}

export interface IBulkDeletePlayer {
  _ids: string[];
  divisionId: string;
  deleteAll?: boolean;
}
